// components/artifacts/charts/BarChartComponent.tsx
import React from "react";
import { BarChart, Bar, CartesianGrid, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { GraphData } from "@/types/Artifact";
import { formatNumber } from "@/lib/utils";

interface BarChartComponentProps {
  data: GraphData;
  config: Record<string, any>;
  visualizationKeys: string[];
  colors: string[];
  id?: string;
}

// Custom Y-axis tick formatter that includes units
const formatYAxisTick = (value: any, unit?: string) => {
  let y = value
  //if value is number, convert to string
  if (typeof value === 'number') {
    y = formatNumber(y);
  } 

  if (!unit) return value;
  
  // Handle different unit types
  switch (unit) {
    case '$':
    case '€':
    case '£':
    case '¥':
      return `${unit}${y}`;
    case '%':
      return `${y}${unit}`;
    default:
      return `${y} ${unit}`;
  }
};

// Custom X-axis tick formatter that includes units
const formatXAxisTick = (value: any, unit?: string) => {
  if (!unit) return value;
  
  // For categories, usually don't need units
  if (!unit || ['category', 'categories'].includes(unit.toLowerCase())) {
    return value;
  }
  
  return `${value} ${unit}`;
};

// Custom tooltip formatter that includes units
const formatTooltipValue = (value: any, unit?: string) => {
  let y = value
  //if value is number, convert to string
  if (typeof value === 'number') {
    y = formatNumber(y);
  } 

  if (!unit) return value;
  
  // Handle different unit types
  switch (unit) {
    case '$':
    case '€':
    case '£':
    case '¥':
      return `${unit}${y}`;
    case '%':
      return `${y}${unit}`;
    default:
      return `${y} ${unit}`;
  }
};

export const BarChartComponent: React.FC<BarChartComponentProps> = React.memo(({
  data,
  config,
  visualizationKeys,
  colors,
  id,
}) => {
  const isStacked = data.type === "stackedBar";
  const { yAxisUnit, xAxisUnit } = data;
  
  return (
    <div className="p-4" id={id}>
      {/* Display unit information if available */}
      {/* {(yAxisUnit || xAxisUnit) && (
        <div className="mb-2 text-sm text-muted-foreground">
          {yAxisUnit && (
            <span className="mr-4">
              Values: {yAxisUnit}
            </span>
          )}
          {xAxisUnit && (
            <span>
              Categories: {xAxisUnit}
            </span>
          )}
        </div>
      )} */}
      
      <ChartContainer
        config={config}
        className="max-h-[calc(100vh-10rem)] min-h-[300px] w-full"
      >
        <BarChart accessibilityLayer data={data.data} margin={{top: 12}}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey={data.xAxis} 
            tickLine={false} 
            tickMargin={10} 
            axisLine={false}
            tickFormatter={(value) => formatXAxisTick(value, xAxisUnit)}
          />
          <YAxis 
            tickLine={false} 
            tickMargin={10} 
            axisLine={false}
            tickFormatter={(value) => formatYAxisTick(value, yAxisUnit)}
          />
          <ChartTooltip 
            content={<ChartTooltipContent 
              valueFormatter={(value) => formatTooltipValue(value, yAxisUnit)}
            />} 
          />
          <ChartLegend content={<ChartLegendContent />} />
          {visualizationKeys.map((key, index) => (
            <Bar 
              key={key} 
              dataKey={key} 
              fill={colors[index]} 
              radius={4}
              stackId={isStacked ? "stack" : undefined}
            />
          ))}
        </BarChart>
      </ChartContainer>
    </div>
  );
});